import React, { useState, useContext, useEffect } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import BackButton from "../components/BackButton";
import { API_URL } from "../config";
import { UserContext } from "../context/UserContext";

function Wallet() {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, fetchUserData } = useContext(UserContext);

    // Local State
    const [transactions, setTransactions] = useState([]);
    const [loadingTxn, setLoadingTxn] = useState(true);
    const [mode, setMode] = useState(location.state?.autoOpenAdd ? "add" : "view");
    const [amountInput, setAmountInput] = useState(location.state?.amountNeeded ? Math.ceil(location.state?.amountNeeded).toString() : "");
    const [processing, setProcessing] = useState(false);

    // Premium Modals aur Withdrawal Method ke liye
    const [alertModal, setAlertModal] = useState({ show: false, message: "", type: "success" });
    const [showKycModal, setShowKycModal] = useState(false);
    const [withdrawMethod, setWithdrawMethod] = useState(""); // "UPI" ya "BANK"

    // Derived State from Context
    const stats = user?.wallet || { deposited: 0, winnings: 0, bonus: 0 };
    const balance = (stats.deposited || 0) + (stats.winnings || 0) + (stats.bonus || 0);

    // KYC Status Check
    const isPanVerified = user?.kyc?.pan?.status === 'VERIFIED';
    const isBankVerified = user?.kyc?.bank?.status === 'VERIFIED';
    const isUpiVerified = user?.kyc?.upi?.status === 'VERIFIED';

    // --- FETCH TRANSACTIONS ---
    useEffect(() => {
        const fetchTransactions = async () => {
            const token = localStorage.getItem("token");
            const currentUserId = user?._id || user?.id || localStorage.getItem("userId");

            if (!token || !currentUserId) return;

            try {
                const res = await axios.get(`${API_URL}/api/payments/transactions/${currentUserId}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                if (res.data.success) {
                    setTransactions(res.data.transactions || []);
                }
            } catch (error) {
                console.error("Txn Fetch Error:", error);
            } finally {
                setLoadingTxn(false);
            }
        };

        if (user || localStorage.getItem("userId")) {
            fetchTransactions();
        }
    }, [user]);

    // --- RAZORPAY SCRIPT ---
    const loadRazorpayScript = () => {
        return new Promise((resolve) => {
            const script = document.createElement('script');
            script.src = 'https://checkout.razorpay.com/v1/checkout.js';
            script.onload = () => resolve(true);
            script.onerror = () => resolve(false);
            document.body.appendChild(script);
        });
    };

    // Handle Withdraw Click (KYC Check)
    const handleWithdrawClick = () => {
        if (!isPanVerified || (!isBankVerified && !isUpiVerified)) {
            setShowKycModal(true);
            return;
        }

        setMode("withdraw");
        if (isUpiVerified) setWithdrawMethod("UPI");
        else if (isBankVerified) setWithdrawMethod("BANK");
    };

    // --- TRANSACTION HANDLER ---
    const handleTransaction = async () => {
        if (!amountInput || parseFloat(amountInput) <= 0) return;
        const token = localStorage.getItem("token");
        const currentUserId = user?._id || user?.id || localStorage.getItem("userId");

        // CASE A: WITHDRAWAL
        if (mode === "withdraw") {
            if (!withdrawMethod) {
                return setAlertModal({ show: true, message: "Please select a withdrawal method.", type: "error" });
            }
            if (parseFloat(amountInput) > (stats.winnings || 0)) {
                return setAlertModal({ show: true, message: "Amount exceeds your Withdrawable Winnings balance.", type: "error" });
            }

            setProcessing(true);
            try {
                const payload = {
                    userId: currentUserId,
                    amount: Number(amountInput),
                    method: withdrawMethod
                };

                if (withdrawMethod === "UPI") payload.upiId = user.kyc.upi.upiId;
                if (withdrawMethod === "BANK") payload.bankAccount = user.kyc.bank.accountNumber;

                const { data } = await axios.post(`${API_URL}/api/payments/withdraw`, payload,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (data.success) {
                    setAlertModal({ show: true, message: "Withdrawal Requested Successfully! It will be processed soon.", type: "success" });
                    await fetchUserData(); // Refresh balance
                    setMode("view");
                    setAmountInput("");
                }
            } catch (error) {
                setAlertModal({ show: true, message: error.response?.data?.message || "Withdrawal Failed", type: "error" });
            } finally {
                setProcessing(false);
            }
            return;
        }

        // CASE B: DEPOSIT
        if (mode === "add") {
            setProcessing(true);
            
            // 🔥 NAYA: Razorpay khulne se pehle returnContext ko safely capture kar lo
            const currentReturnContext = location.state?.returnContext;

            try {
                const isLoaded = await loadRazorpayScript();
                if (!isLoaded) { 
                    setAlertModal({ show: true, message: "Payment Gateway failed to load. Check connection.", type: "error" });
                    setProcessing(false); 
                    return; 
                }

                const { data: orderData } = await axios.post(`${API_URL}/api/payments/deposit/create-order`,
                    { userId: currentUserId, amount: Number(amountInput) },
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                const options = {
                    key: "rzp_test_SAuD7D4VARenR5", // Replace with your Razorpay key
                    amount: orderData.order.amount,
                    currency: "INR",
                    name: "WIN11 Fantasy",
                    description: "Add Cash to Wallet",
                    order_id: orderData.order.id,

                    handler: async function (response) {
                        try {
                            const verifyRes = await axios.post(`${API_URL}/api/payments/deposit/verify`, {
                                razorpay_order_id: response.razorpay_order_id,
                                razorpay_payment_id: response.razorpay_payment_id,
                                razorpay_signature: response.razorpay_signature,
                                amount: Number(amountInput),
                                userId: currentUserId
                            }, { headers: { Authorization: `Bearer ${token}` } });

                            if (verifyRes.data.success) {
                                await fetchUserData(); // Pehle balance update kar lo

                                // 🔥 THE RETURN FIX: Check karo kya hum kisi contest page se aaye the?
                                if (currentReturnContext && currentReturnContext.url) {
                                    // Agar han, toh bina modal dikhaye turant contest page par bhej do!
                                    navigate(currentReturnContext.url, { 
                                        state: {
                                            matchData: currentReturnContext.matchData,
                                            autoOpenContest: currentReturnContext.contestToJoin, // Isey wapas denge taaki auto join kar le
                                            initialTab: currentReturnContext.initialTab
                                        },
                                        replace: true // History clean rakhne ke liye
                                    });
                                } else {
                                    // Agar sidha wallet par aaye the, toh bas success dikha kar view mode me jao
                                    setAlertModal({ show: true, message: "Payment Successful! Amount added to your wallet.", type: "success" });
                                    setMode("view");
                                    setAmountInput("");
                                }
                            }
                        } catch (err) {
                            console.error(err);
                            setAlertModal({ show: true, message: "Payment Verification Failed.", type: "error" });
                        }
                    },
                    theme: { color: "#800000" }
                };
                const rzp = new window.Razorpay(options);
                rzp.open();
            } catch (error) {
                console.error(error);
                setAlertModal({ show: true, message: "Something went wrong while initiating payment.", type: "error" });
            } finally {
                setProcessing(false);
            }
        }
    };

    const formatCurrency = (num) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(num);

    const formatDate = (dateString) => {
        const options = { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
        return new Date(dateString).toLocaleDateString('en-IN', options);
    };

    // --- SHIMMER LOADER ---
    if (!user) return (
        <div style={{ minHeight: "100vh", background: "#f3f4f6" }}>
            <div className="pb-5 pt-2" style={{ background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)", borderBottomLeftRadius: "35px", borderBottomRightRadius: "35px", height: "340px" }}>
                <div className="container-fluid px-3 placeholder-glow">
                    <BackButton to="/home" label="" className="text-white" />
                    <div className="card border-0 mt-2 shadow-lg rounded-4 overflow-hidden" style={{ height: "200px", background: "rgba(255,255,255,0.1)" }}></div>
                </div>
            </div>
        </div>
    );

    return (
        <div style={{ minHeight: "100vh", background: "#f3f4f6", paddingBottom: "100px" }}>

            {/* HEADER SECTION IN MAROON */}
            <div
                className="pb-5 pt-2"
                style={{
                    background: "linear-gradient(135deg, #800000 0%, #4A0000 100%)",
                    borderBottomLeftRadius: "35px",
                    borderBottomRightRadius: "35px",
                    height: "340px",
                    boxShadow: "0 10px 20px rgba(128,0,0,0.15)"
                }}
            >
                <div className="container-fluid px-3">
                    <BackButton to="/home" label="" className="text-white" />

                    <div className="card border-0 shadow-lg mt-2 text-white position-relative overflow-hidden"
                        style={{
                            borderRadius: "24px",
                            background: "linear-gradient(135deg, #A00000 0%, #6A0000 100%)",
                            minHeight: "200px"
                        }}>
                        <div className="position-absolute top-0 end-0 p-4 opacity-25">
                            <i className="bi bi-wallet2 display-1"></i>
                        </div>
                        <div className="position-absolute bottom-0 start-0 p-4 opacity-10" style={{ transform: "rotate(45deg) translate(-20px, 40px)" }}>
                            <i className="bi bi-currency-rupee display-1"></i>
                        </div>

                        <div className="card-body p-4 d-flex flex-column justify-content-between position-relative z-1">
                            <div>
                                <span className="badge bg-white bg-opacity-25 border border-white border-opacity-25 rounded-pill fw-normal px-3 mb-2 shadow-sm">
                                    Total Balance
                                </span>
                                <h1 className="fw-bold display-5 mb-0">{formatCurrency(balance)}</h1>
                            </div>

                            <div className="d-flex justify-content-between align-items-end mt-4">
                                <div>
                                    <small className=" d-block text-uppercase fw-bold text-warning" style={{ fontSize: "12px", letterSpacing: "1px" }}>Account Holder</small>
                                    <span className="fw-bold text-uppercase">
                                        {user.name || "USER"}
                                    </span>
                                </div>
                                <div>
                                    <i className="bi bi-credit-card-2-front fs-1 opacity-75"></i>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="container-fluid px-3 px-md-4" style={{ marginTop: "-80px", position: "relative", zIndex: 10 }}>

                {/* === ACTION MODE (DEPOSIT / WITHDRAW) === */}
                {mode !== "view" && (
                    <div className="card border-0 shadow-lg mb-4 animate-slide-down" style={{ borderRadius: "24px" }}>
                        <div className="card-body p-4">
                            <div className="d-flex justify-content-between align-items-center mb-4">
                                <h5 className="fw-bold mb-0 text-dark">
                                    {mode === "add" ? "Add Money" : "Withdraw Funds"}
                                </h5>
                                <button className="btn btn-sm btn-light rounded-circle" onClick={() => setMode("view")}>
                                    <i className="bi bi-x-lg text-dark"></i>
                                </button>
                            </div>

                            {/* Amount Input */}
                            <div className="input-group mb-4 border-bottom border-2 pb-2">
                                <span className="input-group-text bg-transparent border-0 fs-2 fw-bold text-muted">₹</span>
                                <input
                                    type="number"
                                    className="form-control bg-transparent border-0 fs-1 fw-bold text-dark shadow-none ps-2"
                                    placeholder="0"
                                    value={amountInput}
                                    onChange={(e) => setAmountInput(e.target.value)}
                                    autoFocus
                                />
                            </div>

                            {/* WITHDRAW UI (Bank / UPI Select) */}
                            {mode === "withdraw" && (
                                <div className="mb-4 text-start">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <label className="fw-bold text-muted small">Select Withdrawal Method</label>
                                        <small className="text-success fw-bold">Withdrawable: ₹{stats.winnings || 0}</small>
                                    </div>
                                    <div className="d-flex flex-column gap-3">

                                        {/* UPI Option */}
                                        {isUpiVerified && (
                                            <div
                                                className={`p-3 rounded-4 border shadow-sm transition-all ${withdrawMethod === "UPI" ? "border-danger bg-danger bg-opacity-10" : "border-light bg-light"}`}
                                                onClick={() => setWithdrawMethod("UPI")}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="rounded-circle d-flex align-items-center justify-content-center bg-white shadow-sm text-danger" style={{ width: '45px', height: '45px' }}>
                                                        <i className="bi bi-qr-code fs-5"></i>
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0 fw-bold text-dark">UPI ID</h6>
                                                        <small className="text-muted">{user.kyc.upi.upiId}</small>
                                                    </div>
                                                    {withdrawMethod === "UPI" && <i className="bi bi-check-circle-fill text-danger ms-auto fs-4"></i>}
                                                </div>
                                            </div>
                                        )}

                                        {/* Bank Option */}
                                        {isBankVerified && (
                                            <div
                                                className={`p-3 rounded-4 border shadow-sm transition-all ${withdrawMethod === "BANK" ? "border-danger bg-danger bg-opacity-10" : "border-light bg-light"}`}
                                                onClick={() => setWithdrawMethod("BANK")}
                                                style={{ cursor: "pointer" }}
                                            >
                                                <div className="d-flex align-items-center gap-3">
                                                    <div className="rounded-circle d-flex align-items-center justify-content-center bg-white shadow-sm text-danger" style={{ width: '45px', height: '45px' }}>
                                                        <i className="bi bi-bank fs-5"></i>
                                                    </div>
                                                    <div>
                                                        <h6 className="mb-0 fw-bold text-dark">Bank Account</h6>
                                                        <small className="text-muted">A/c: **** {user.kyc.bank.accountNumber.slice(-4)}</small>
                                                    </div>
                                                    {withdrawMethod === "BANK" && <i className="bi bi-check-circle-fill text-danger ms-auto fs-4"></i>}
                                                </div>
                                            </div>
                                        )}

                                    </div>
                                </div>
                            )}

                            {/* DEPOSIT UI (Amount Buttons) */}
                            {mode === "add" && (
                                <div className="d-flex gap-2 mb-4 overflow-auto pb-2 custom-scrollbar">
                                    {[100, 500, 1000, 2000].map(amt => (
                                        <button
                                            key={amt}
                                            onClick={() => setAmountInput(amt.toString())}
                                            className="btn btn-outline-dark rounded-pill px-4 py-2 flex-shrink-0 fw-bold border-opacity-25 shadow-sm"
                                        >
                                            +₹{amt}
                                        </button>
                                    ))}
                                </div>
                            )}

                            <button
                                className={`btn w-100 py-3 rounded-pill fw-bold shadow-sm text-white d-flex align-items-center justify-content-center gap-2`}
                                style={{ background: mode === "add" ? "linear-gradient(135deg, #198754, #2ecc71)" : "linear-gradient(135deg, #800000, #4A0000)", border: "none" }}
                                onClick={handleTransaction}
                                disabled={processing || !amountInput}
                            >
                                {processing ? (
                                    <><span className="spinner-border spinner-border-sm" role="status"></span> Processing...</>
                                ) : (
                                    mode === "add" ? <><i className="bi bi-shield-lock-fill"></i> PROCEED TO PAY</> : <><i className="bi bi-cash-coin"></i> REQUEST WITHDRAWAL</>
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* === VIEW MODE === */}
                {mode === "view" && (
                    <div className="animate-fade-in">
                        <div className="card border-0 shadow-sm mb-4" style={{ borderRadius: "20px" }}>
                            <div className="card-body p-3">
                                <div className="row text-center g-0">
                                    <div className="col-4 border-end">
                                        <small className=" fw-bold d-block mb-1" style={{ fontSize: "10px" }}>DEPOSIT</small>
                                        <span className="fw-bold text-dark">₹{Number(stats.deposited || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="col-4 border-end">
                                        <small className=" fw-bold d-block mb-1" style={{ fontSize: "10px" }}>WINNINGS</small>
                                        <span className="fw-bold text-success">₹{Number(stats.winnings || 0).toFixed(2)}</span>
                                    </div>
                                    <div className="col-4">
                                        <small className=" fw-bold d-block mb-1" style={{ fontSize: "10px" }}>CASH BONUS</small>
                                        <span className="fw-bold text-primary">₹{Number(stats.bonus || 0).toFixed(2)}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="row g-3 mb-4">
                            <div className="col-6">
                                <button
                                    className="btn w-100 p-3 rounded-4 shadow-sm d-flex flex-column align-items-center justify-content-center border-0 text-white"
                                    style={{ background: "linear-gradient(135deg, #198754, #2ecc71)" }}
                                    onClick={() => setMode("add")}
                                >
                                    <div className="rounded-circle bg-white bg-opacity-25 d-flex align-items-center justify-content-center mb-2" style={{ width: "40px", height: "40px" }}>
                                        <i className="bi bi-plus-lg text-white"></i>
                                    </div>
                                    <span className="fw-bold">Add Cash</span>
                                </button>
                            </div>
                            <div className="col-6">
                                <button
                                    className="btn w-100 p-3 rounded-4 shadow-sm d-flex flex-column align-items-center justify-content-center border-0 text-white"
                                    style={{ background: "linear-gradient(135deg, #800000, #4A0000)" }}
                                    onClick={handleWithdrawClick}
                                >
                                    <div className="rounded-circle bg-white bg-opacity-25 d-flex align-items-center justify-content-center mb-2" style={{ width: "40px", height: "40px" }}>
                                        <i className="bi bi-bank text-white"></i>
                                    </div>
                                    <span className="fw-bold">Withdraw</span>
                                </button>
                            </div>
                        </div>

                        <div>
                            <h6 className="fw-bold text-muted mb-3 ps-1">RECENT TRANSACTIONS</h6>
                            {loadingTxn ? (
                                <div className="text-center py-4"><span className="spinner-border spinner-border-sm text-muted"></span></div>
                            ) : transactions.length === 0 ? (
                                <div className="text-center mt-5">
                                    <i className="bi bi-receipt text-muted opacity-25" style={{ fontSize: "3rem" }}></i>
                                    <p className="text-muted small mt-2">No transactions yet.</p>
                                </div>
                            ) : (
                                <div className="d-flex flex-column gap-3">
                                    {transactions.map(txn => {
                                        // 🔥 FIX: ADDED BONUS LOGIC HERE 🔥
                                        let icon = "circle";
                                        let colorClass = "text-dark";
                                        let bgClass = "bg-light";
                                        let title = "Transaction";
                                        let sign = "-";

                                        if (txn.type === "DEPOSIT") {
                                            icon = "plus-lg"; colorClass = "text-success"; bgClass = "bg-success-subtle"; title = "Added to Wallet"; sign = "+";
                                        } else if (txn.type === "WITHDRAWAL") {
                                            icon = "bank2"; colorClass = "text-danger"; bgClass = "bg-danger-subtle"; title = "Withdrawal Request"; sign = "-";
                                        } else if (txn.type === "WINNING") {
                                            icon = "trophy-fill"; colorClass = "text-success"; bgClass = "bg-success-subtle"; title = "Contest Won"; sign = "+";
                                        } else if (txn.type === "REFERRAL_BONUS" || txn.type === "SIGNUP_BONUS") {
                                            icon = "gift-fill"; colorClass = "text-success"; bgClass = "bg-success-subtle"; title = "Bonus Added"; sign = "+";
                                        }

                                        return (
                                            <div key={txn._id} className="card border-0 shadow-sm" style={{ borderRadius: "16px" }}>
                                                <div className="card-body p-3 d-flex align-items-center">
                                                    <div className={`rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0 ${bgClass} ${colorClass}`} style={{ width: "45px", height: "45px" }}>
                                                        <i className={`bi bi-${icon} fs-5`}></i>
                                                    </div>
                                                    <div className="flex-grow-1">
                                                        <h6 className="fw-bold mb-0 text-dark" style={{ fontSize: "0.95rem" }}>{title}</h6>
                                                        <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                                                            {formatDate(txn.date)} • <span className={txn.status === 'SUCCESS' ? 'text-success fw-bold' : 'text-warning fw-bold'}>{txn.status}</span>
                                                        </small>
                                                    </div>
                                                    <div className={`fw-bold fs-6 ${colorClass}`}>
                                                        {sign} ₹{txn.amount}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {/* KYC WARNING MODAL */}
            {showKycModal && (
                <>
                    <div className="modal-backdrop fade show" style={{ backdropFilter: "blur(5px)", backgroundColor: "rgba(0,0,0,0.7)", zIndex: 1080 }}></div>
                    <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1090 }}>
                        <div className="modal-dialog modal-dialog-centered px-4">
                            <div className="modal-content border-0 shadow-lg rounded-4 p-4 text-center animate-bounce">
                                <div className="mb-3 text-warning">
                                    <i className="bi bi-shield-exclamation display-1"></i>
                                </div>
                                <h4 className="fw-bold mb-2 text-dark">KYC Incomplete!</h4>
                                <p className="text-muted mb-4 px-2" style={{ fontSize: "15px" }}>
                                    You need to verify your PAN Card and Bank Account / UPI to withdraw winnings.
                                </p>
                                <div className="d-flex gap-2">
                                    <button className="btn btn-light w-50 rounded-pill fw-bold" onClick={() => setShowKycModal(false)}>Cancel</button>
                                    <button
                                        className="btn w-50 rounded-pill fw-bold text-white shadow-sm"
                                        style={{ background: "linear-gradient(135deg, #800000, #4A0000)" }}
                                        onClick={() => navigate("/verify-account")}
                                    >
                                        Verify Now
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* PREMIUM ALERT MODAL */}
            {alertModal.show && (
                <>
                    <div className="modal-backdrop fade show" style={{ backdropFilter: "blur(5px)", backgroundColor: "rgba(0,0,0,0.7)", zIndex: 1080 }}></div>
                    <div className="modal fade show d-block" tabIndex="-1" style={{ zIndex: 1090 }}>
                        <div className="modal-dialog modal-dialog-centered px-4">
                            <div className="modal-content border-0 shadow-lg rounded-4 p-4 text-center animate-bounce">
                                <div className={`mb-3 text-${alertModal.type === 'error' ? 'danger' : 'success'}`}>
                                    <i className={`bi bi-${alertModal.type === 'error' ? 'x-circle-fill' : 'check-circle-fill'} display-1`}></i>
                                </div>
                                <h4 className="fw-bold mb-2 text-dark">
                                    {alertModal.type === 'error' ? 'Oops!' : 'Success!'}
                                </h4>
                                <p className="text-muted mb-4 px-2" style={{ fontSize: "15px" }}>
                                    {alertModal.message}
                                </p>
                                <button
                                    className={`btn w-100 rounded-pill py-3 fw-bold shadow-sm text-white`}
                                    style={{ background: alertModal.type === 'error' ? '#dc3545' : '#198754', border: "none" }}
                                    onClick={() => setAlertModal({ show: false, message: "", type: "success" })}
                                >
                                    Okay
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <style>{`
                .animate-slide-down { animation: slideDown 0.3s ease-out; }
                .animate-fade-in { animation: fadeIn 0.4s ease-out; }
                .animate-bounce { animation: bounceIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) both; }
                
                @keyframes slideDown { from { transform: translateY(-20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes bounceIn {
                    0% { transform: scale(0.5); opacity: 0; }
                    80% { transform: scale(1.05); opacity: 1; }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                /* Hide scrollbar for amount pills */
                .custom-scrollbar::-webkit-scrollbar { display: none; }
                .custom-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
                
                .transition-all { transition: all 0.2s ease-in-out; }
            `}</style>
        </div>
    );
}

export default Wallet;